import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { trackEvent, BOOKING_SCREENING_URL, PHONE_NUMBER, PHONE_TEL } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function Header() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileHelpOpen, setMobileHelpOpen] = useState(false);
  const [helpDropdownOpen, setHelpDropdownOpen] = useState(false);
  const [secondaryDropdownOpen, setSecondaryDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const helpDropdownRef = useRef(null);
  const secondaryDropdownRef = useRef(null);
  const helpTimeoutRef = useRef(null);
  const secondaryTimeoutRef = useRef(null);

  const handleHelpMouseEnter = () => {
    if (helpTimeoutRef.current) clearTimeout(helpTimeoutRef.current);
    setHelpDropdownOpen(true);
  };

  const handleHelpMouseLeave = () => {
    if (helpTimeoutRef.current) clearTimeout(helpTimeoutRef.current);
    helpTimeoutRef.current = setTimeout(() => {
      setHelpDropdownOpen(false);
    }, 180);
  };

  const handleSecondaryMouseEnter = () => {
    if (secondaryTimeoutRef.current) clearTimeout(secondaryTimeoutRef.current);
    setSecondaryDropdownOpen(true);
  };

  const handleSecondaryMouseLeave = () => {
    if (secondaryTimeoutRef.current) clearTimeout(secondaryTimeoutRef.current);
    secondaryTimeoutRef.current = setTimeout(() => {
      setSecondaryDropdownOpen(false);
    }, 180);
  };

  // Detect scroll to style fixed/sticky header
  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 20);
    }
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    if (helpTimeoutRef.current) clearTimeout(helpTimeoutRef.current);
    if (secondaryTimeoutRef.current) clearTimeout(secondaryTimeoutRef.current);
    setMobileMenuOpen(false);
    setMobileHelpOpen(false);
    setMobileServicesOpen(false);
    setHelpDropdownOpen(false);
    setSecondaryDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (helpTimeoutRef.current) clearTimeout(helpTimeoutRef.current);
      if (secondaryTimeoutRef.current) clearTimeout(secondaryTimeoutRef.current);
    };
  }, []);

  // Click outside to close desktop dropdowns (only when mobile menu is closed)
  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuOpen) return;
      if (helpDropdownRef.current && !helpDropdownRef.current.contains(event.target)) {
        setHelpDropdownOpen(false);
      }
      if (secondaryDropdownRef.current && !secondaryDropdownRef.current.contains(event.target)) {
        setSecondaryDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  const problemAreas = [
    { title: 'Hypnose', href: '/hypnose/', desc: 'Dybdegående arbejde med det ubevidste sind og vaner' },
    { title: 'Traumer', href: '/traumer/', desc: 'Skånsom bearbejdning af chok og svigt (TBT)' },
    { title: 'Stress', href: '/stress/', desc: 'Berolig nervesystemet og genfind overskuddet' },
    { title: 'Angst', href: '/angst/', desc: 'Lær at regulere krop og tanker ved uro og panik' },
    { title: 'Depression', href: '/depression/', desc: 'Genfind livsglæde og mening i et trygt rum' },
    { title: 'Lavt selvværd', href: '/lavt-selvvaerd/', desc: 'Slip den indre selvkritik og opbyg stabilitet' },
    { title: 'Sorg & krise', href: '/sorg-og-krise/', desc: 'Nærværende støtte ved tab og livsforandring' },
    { title: 'Vaner: rygestop & sukkertrang', href: '/hypnose-vaner/', desc: 'Rygestop, sukkertrang og uønskede vaner' },
  ];

  const ydelserServices = [
    { title: 'Gruppesupervision', href: '/gruppesupervision/', desc: 'For psykoterapeuter og fagpersoner' },
    { title: 'Konflikthåndtering', href: '/konflikthaandtering/', desc: 'Mægling og dialog i organisationer og teams' },
    { title: 'Foredrag & workshops', href: '/foredrag-og-workshops/', desc: 'Praksisnære oplæg om hjerne og relationer' },
    { title: 'Coaching for ledere og virksomheder', href: '/coaching-for-ledere-og-virksomheder/', desc: 'Målrettet udvikling for ledere og teams' },
    { title: 'Erhvervspsykoterapeut', href: '/erhvervspsykoterapeut/', desc: 'Psykologisk tryghed, trivsel og ledelsessparring' },
  ];

  const isProblemAreaActive = problemAreas.some((p) => location.pathname.startsWith(p.href));
  const isSecondaryActive = ydelserServices.some((s) => location.pathname.startsWith(s.href)) || location.pathname.startsWith('/supervision') || location.pathname.startsWith('/coaching');

  const isHomePage = location.pathname === '/';

  return (
    <header
      className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#0F141C]/85 backdrop-blur-md shadow-lg border-b border-white/10'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      {/* Main navigation container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'min-h-[72px] py-1.5' : 'min-h-[112px] py-2'}`}>
          {/* Brand Logo */}
          <Link
            to="/"
            className="flex items-center group shrink-0 rounded-2xl overflow-hidden"
            aria-label="Psykoterapeut Kim Palasz - Til forsiden"
          >
            <img
              src={optimizeImage(
                'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/logo-fra-robert.png',
                240
              )}
              alt="Psykoterapeut Kim Palasz"
              className={`w-auto object-contain transition-all duration-300 rounded-2xl ${
                isScrolled ? 'h-[58px] sm:h-[64px]' : 'h-[100px]'
              }`}
            />
          </Link>

          {/* Desktop Navigation: Visible strictly at screens >= 1200px */}
          <nav className={`hidden min-[1200px]:flex items-center gap-1 xl:gap-2 text-sm font-medium ${isHomePage || isScrolled ? 'text-white' : 'text-[#11161d]'}`}>
            <Link
              to="/"
              className={`px-3 py-2 rounded-lg transition-colors ${
                location.pathname === '/'
                  ? isHomePage || isScrolled
                    ? 'text-white bg-white/20 font-semibold'
                    : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                  : isHomePage || isScrolled
                    ? 'text-white/90 hover:text-white hover:bg-white/15'
                    : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
              }`}
            >
              Forside
            </Link>

            {/* "Få hjælp til" Dropdown */}
            <div
              ref={helpDropdownRef}
              className="relative"
              onMouseEnter={handleHelpMouseEnter}
              onMouseLeave={handleHelpMouseLeave}
            >
              <button
                type="button"
                onClick={() => setHelpDropdownOpen(!helpDropdownOpen)}
                aria-expanded={helpDropdownOpen}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                  isProblemAreaActive || helpDropdownOpen
                    ? isHomePage || isScrolled
                      ? 'text-white bg-white/20 font-semibold'
                      : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                    : isHomePage || isScrolled
                      ? 'text-white/90 hover:text-white hover:bg-white/15'
                      : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
                }`}
              >
                <span>Få hjælp til</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    helpDropdownOpen
                      ? isHomePage || isScrolled ? 'rotate-180 text-white' : 'rotate-180 text-[#5A232E]'
                      : isHomePage || isScrolled ? 'text-white/70' : 'text-[#6c7480]'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {helpDropdownOpen && (
                <div className="absolute left-0 top-full pt-2 w-80 z-50">
                  <div className="bg-white rounded-2xl shadow-2xl border border-[#D9DDE2] p-3 animate-in fade-in duration-150">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6c7480] px-3 py-1.5 border-b border-[#D9DDE2]/50 mb-1">
                      Problemområder
                    </div>
                    <div className="grid gap-1">
                      {problemAreas.map((area) => (
                        <Link
                          key={area.href}
                          to={area.href}
                          className={`p-2.5 rounded-xl transition-all block ${
                            location.pathname === area.href
                              ? 'bg-[#ECEEF1] text-[#5A232E] border-l-3 border-[#5A232E]'
                              : 'hover:bg-[#ECEEF1] text-[#11161d]'
                          }`}
                        >
                          <div className="font-medium text-sm text-[#5A232E] flex items-center justify-between">
                            {area.title}
                            <i className="fa-solid fa-arrow-right text-[11px] text-[#5A232E] opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="text-xs text-[#6c7480] mt-0.5">{area.desc}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/terapi-og-hypnose/"
              className={`px-3 py-2 rounded-lg transition-colors ${
                location.pathname.startsWith('/terapi-og-hypnose')
                  ? isHomePage || isScrolled
                    ? 'text-white bg-white/20 font-semibold'
                    : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                  : isHomePage || isScrolled
                    ? 'text-white/90 hover:text-white hover:bg-white/15'
                    : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
              }`}
            >
              Terapi & hypnose
            </Link>

            <Link
              to="/om-kim/"
              className={`px-3 py-2 rounded-lg transition-colors ${
                location.pathname.startsWith('/om-kim')
                  ? isHomePage || isScrolled
                    ? 'text-white bg-white/20 font-semibold'
                    : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                  : isHomePage || isScrolled
                    ? 'text-white/90 hover:text-white hover:bg-white/15'
                    : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
              }`}
            >
              Om Kim
            </Link>

            <Link
              to="/priser/"
              className={`px-3 py-2 rounded-lg transition-colors ${
                location.pathname.startsWith('/priser')
                  ? isHomePage || isScrolled
                    ? 'text-white bg-white/20 font-semibold'
                    : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                  : isHomePage || isScrolled
                    ? 'text-white/90 hover:text-white hover:bg-white/15'
                    : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
              }`}
            >
              Priser
            </Link>

            {/* Erhverv Dropdown */}
            <div
              ref={secondaryDropdownRef}
              className="relative"
              onMouseEnter={handleSecondaryMouseEnter}
              onMouseLeave={handleSecondaryMouseLeave}
            >
              <button
                type="button"
                onClick={() => setSecondaryDropdownOpen(!secondaryDropdownOpen)}
                aria-expanded={secondaryDropdownOpen}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                  isSecondaryActive || secondaryDropdownOpen
                    ? isHomePage || isScrolled
                      ? 'text-white bg-white/20 font-semibold'
                      : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                    : isHomePage || isScrolled
                      ? 'text-white/90 hover:text-white hover:bg-white/15'
                      : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
                }`}
              >
                <span>Erhverv</span>
                <i
                  className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                    secondaryDropdownOpen
                      ? isHomePage || isScrolled ? 'rotate-180 text-white' : 'rotate-180 text-[#5A232E]'
                      : isHomePage || isScrolled ? 'text-white/70' : 'text-[#6c7480]'
                  }`}
                  aria-hidden="true"
                />
              </button>

              {secondaryDropdownOpen && (
                <div className="absolute left-0 top-full pt-2 w-80 z-50">
                  <div className="bg-white rounded-2xl shadow-2xl border border-[#D9DDE2] p-2 animate-in fade-in duration-150">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6c7480] px-3 py-1 border-b border-[#D9DDE2]/50 mb-1">
                      Erhverv
                    </div>
                    {ydelserServices.map((service) => (
                      <Link
                        key={service.href}
                        to={service.href}
                        className={`p-2.5 rounded-xl transition-all block ${
                          location.pathname.startsWith(service.href)
                            ? 'bg-[#ECEEF1] text-[#5A232E] font-semibold border-l-3 border-[#5A232E]'
                            : 'hover:bg-[#ECEEF1] text-[#11161d]'
                        }`}
                      >
                        <div className="font-medium text-sm text-[#5A232E]">{service.title}</div>
                        <div className="text-xs text-[#6c7480] mt-0.5">{service.desc}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/artikler/"
              className={`px-3 py-2 rounded-lg transition-colors ${
                location.pathname.startsWith('/artikler')
                  ? isHomePage || isScrolled
                    ? 'text-white bg-white/20 font-semibold'
                    : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                  : isHomePage || isScrolled
                    ? 'text-white/90 hover:text-white hover:bg-white/15'
                    : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
              }`}
            >
              Artikler
            </Link>

            <Link
              to="/kontakt/"
              className={`px-3 py-2 rounded-lg transition-colors ${
                location.pathname.startsWith('/kontakt')
                  ? isHomePage || isScrolled
                    ? 'text-white bg-white/20 font-semibold'
                    : 'text-[#5A232E] bg-[#D9DDE2] font-semibold'
                  : isHomePage || isScrolled
                    ? 'text-white/90 hover:text-white hover:bg-white/15'
                    : 'hover:text-[#5A232E] hover:bg-[#D9DDE2]/60'
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Desktop Primary CTA Button */}
          <div className="hidden min-[1200px]:flex items-center gap-3">
            <a
              href={BOOKING_SCREENING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cta_booking_click', { location: 'header_desktop' })}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-sm hover:shadow-md transition-all transform hover:-translate-y-0.5 cursor-pointer no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check text-xs" aria-hidden="true" />
              <span>Book gratis samtale</span>
            </a>
          </div>

          {/* Burger Button: Strictly shown below 1200px */}
          <div className="flex min-[1200px]:hidden items-center gap-2">
            <a
              href={BOOKING_SCREENING_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('cta_booking_click', { location: 'header_mobile_compact' })}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#5A232E] text-white font-medium text-xs no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span className="hidden sm:inline">Book samtale</span>
              <span className="sm:hidden">Book</span>
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn menu"
              className={`p-2.5 rounded-xl transition-colors focus:outline-hidden focus:ring-2 focus:ring-[#5A232E] ${
                mobileMenuOpen
                  ? 'text-black bg-white/90 hover:bg-white'
                  : isHomePage || isScrolled
                  ? 'text-white hover:bg-white/20'
                  : 'text-[#5A232E] hover:bg-[#D9DDE2]'
              }`}
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-black' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay for screens < 1200px */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-[#D9DDE2] px-4 pt-3 pb-6 shadow-2xl max-h-[85vh] overflow-y-auto animate-in slide-in-from-top-4 duration-200">
          <div className="space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                location.pathname === '/'
                  ? 'bg-[#5A232E] text-white shadow-xs'
                  : 'text-[#11161d] hover:bg-[#ECEEF1]'
              }`}
            >
              Forside
            </Link>

            {/* Mobile Accordion: Få hjælp til */}
            <div className="border border-[#D9DDE2] rounded-xl overflow-hidden my-2">
              <button
                type="button"
                onClick={() => setMobileHelpOpen(!mobileHelpOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm font-semibold transition-colors ${
                  problemAreas.some((p) => location.pathname.startsWith(p.href))
                    ? 'bg-[#5A232E]/10 text-[#5A232E]'
                    : 'bg-[#ECEEF1] text-[#5A232E]'
                }`}
              >
                <span>Få hjælp til (Problemområder)</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileHelpOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              {mobileHelpOpen && (
                <div className="p-2 bg-white space-y-1 border-t border-[#D9DDE2]">
                  {problemAreas.map((area) => {
                    const isActive = location.pathname.startsWith(area.href);
                    return (
                      <Link
                        key={area.href}
                        to={area.href}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileHelpOpen(false);
                        }}
                        className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive
                            ? 'bg-[#5A232E] text-white font-semibold'
                            : 'text-[#11161d] hover:bg-[#ECEEF1] hover:text-[#5A232E]'
                        }`}
                      >
                        <div className="font-medium">{area.title}</div>
                        <div className={`text-xs ${isActive ? 'text-white/80' : 'text-[#6c7480]'}`}>{area.desc}</div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <Link
              to="/terapi-og-hypnose/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                location.pathname.startsWith('/terapi-og-hypnose')
                  ? 'bg-[#5A232E] text-white shadow-xs'
                  : 'text-[#11161d] hover:bg-[#ECEEF1]'
              }`}
            >
              Terapi & hypnose
            </Link>

            <Link
              to="/om-kim/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                location.pathname.startsWith('/om-kim')
                  ? 'bg-[#5A232E] text-white shadow-xs'
                  : 'text-[#11161d] hover:bg-[#ECEEF1]'
              }`}
            >
              Om Kim
            </Link>

            <Link
              to="/priser/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                location.pathname.startsWith('/priser')
                  ? 'bg-[#5A232E] text-white shadow-xs'
                  : 'text-[#11161d] hover:bg-[#ECEEF1]'
              }`}
            >
              Priser
            </Link>

            {/* Mobile Accordion: Erhverv */}
            <div className="border border-[#D9DDE2] rounded-xl overflow-hidden my-2">
              <button
                type="button"
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm font-semibold transition-colors ${
                  isSecondaryActive
                    ? 'bg-[#5A232E]/10 text-[#5A232E]'
                    : 'bg-[#ECEEF1] text-[#5A232E]'
                }`}
              >
                <span>Erhverv</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              {mobileServicesOpen && (
                <div className="p-2 bg-white space-y-1 border-t border-[#D9DDE2]">
                  {ydelserServices.map((service) => {
                    const isActive = location.pathname.startsWith(service.href);
                    return (
                      <Link
                        key={service.href}
                        to={service.href}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setMobileServicesOpen(false);
                        }}
                        className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive
                            ? 'bg-[#5A232E] text-white font-semibold'
                            : 'text-[#11161d] hover:bg-[#ECEEF1] hover:text-[#5A232E]'
                        }`}
                      >
                        <div className="font-medium">{service.title}</div>
                        <div className={`text-xs ${isActive ? 'text-white/80' : 'text-[#6c7480]'}`}>{service.desc}</div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            <Link
              to="/artikler/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                location.pathname.startsWith('/artikler') || location.pathname.startsWith('/blog')
                  ? 'bg-[#5A232E] text-white shadow-xs'
                  : 'text-[#11161d] hover:bg-[#ECEEF1]'
              }`}
            >
              Artikler
            </Link>

            <Link
              to="/kontakt/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block px-4 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                location.pathname.startsWith('/kontakt')
                  ? 'bg-[#5A232E] text-white shadow-xs'
                  : 'text-[#11161d] hover:bg-[#ECEEF1]'
              }`}
            >
              Kontakt
            </Link>

            {/* Mobile Booking Action */}
            <div className="pt-4 mt-4 border-t border-[#D9DDE2]">
              <a
                href={BOOKING_SCREENING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('cta_booking_click', { location: 'mobile_menu_cta' })}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#5A232E] text-white font-semibold text-sm shadow-sm"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book en gratis afklaringssamtale</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
